import React, { useState } from "react";
import {
  Box,
  Button,
  Group,
  Popover,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import moment from "moment-timezone";
import { useCalendar } from "../../../../../context/CalendarContext";
import { useResponsive } from "../../../../../context/ResponsiveContext";
import { useSalon } from "../../../../../context/SalonContext";
import { DESIGN_ICONS } from "../../../../../theme/designIcons";
import { DEFAULT_TIMEZONE } from "../../../../../utils/timeUtils";
import StandardizedDatePicker from "../../../../common/StandardizedDatePicker";
import FilterDropdown, {
  countActiveFilterGroups,
  FilterCountWindow,
} from "./FilterDropdown";
import { dateTileModel } from "./dateTileModel";
import WeekDisplayMenu from "./WeekDisplayMenu";
import ZoomControl from "./ZoomControl";
import styles from "./CalendarToolbar.module.css";

const {
  ChevronDown: IconChevronDown,
  ChevronLeft: IconChevronLeft,
  ChevronRight: IconChevronRight,
  Filter: IconFilter,
} = DESIGN_ICONS;

const WIRE_DATE_FORMAT = "YYYY-MM-DD";

type NavigateAction = "PREV" | "NEXT" | "TODAY";

interface CalendarToolbarProps {
  /** Viewed date. Unused when `filtersOnly`. */
  date?: Date;
  view: "day" | "week" | "card" | "list";
  onNavigate?: (action: NavigateAction) => void;
  /** List view: render only the Filters pill (no date title, no prev/today/
   *  next). The list is today-anchored, so there's no date to navigate. */
  filtersOnly?: boolean;
}

/** FilterDropdown per-staff count window for each view. */
const countWindowFor = (
  view: CalendarToolbarProps["view"],
): FilterCountWindow =>
  view === "week" ? "week" : view === "list" ? "list" : "day";

/**
 * Toolbar above every calendar view. Day/Week render it through RBC's
 * `components.toolbar`, so it must be mounted by a module-level wrapper (see
 * DayView/WeekView): its local popover state (date picker, Filters) then
 * survives filter changes and day navigation. All changing data (filters,
 * dateRange, the salon's today) is read from context here rather than
 * passed in.
 *
 * Desktop row (design handoff header): the date tile (opens the date
 * picker) on the left; on the right, in order: [week-only Display, 04] →
 * Filters → [Zoom, 05] → divider → Prev / Today / Next.
 */
const CalendarToolbar: React.FC<CalendarToolbarProps> = ({
  date,
  view,
  onNavigate,
  filtersOnly = false,
}) => {
  const { isMobile } = useResponsive();
  const { businessInfo } = useSalon() as {
    businessInfo: { timezone?: string } | null;
  };
  const {
    setSelectedDate,
    selectedArtists,
    selectedServiceTypes,
    selectedStatuses,
    dateRange,
  } = useCalendar();

  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const activeFilterGroups = countActiveFilterGroups(
    selectedArtists,
    selectedServiceTypes,
    selectedStatuses,
  );

  const handleDatePick = (value: string | null) => {
    if (!value) return;
    setSelectedDate(new Date(`${value}T00:00:00`));
    setDatePickerOpen(false);
  };

  // Mobile date label (desktop shows the date tile).
  const getDateLabel = () => {
    if (view === "week" && dateRange) {
      return moment(dateRange.start).format("MMMM YYYY");
    }
    if (view === "day" || view === "card") {
      return moment(date).format("ddd, MMM D, YYYY");
    }
    return moment(date).format("MMMM YYYY");
  };

  // The Filters pill is desktop-only; on mobile a filters-only toolbar has
  // nothing to show.
  if (filtersOnly && isMobile) return null;

  const filtersPill = !isMobile && (
    <Popover
      opened={filtersOpen}
      onChange={setFiltersOpen}
      position="bottom-end"
      shadow="md"
      radius={10}
      width={680}
      withinPortal
    >
      <Popover.Target>
        <UnstyledButton
          className={`${styles.control} ${styles.filtersControl}`}
          data-active={activeFilterGroups > 0 || undefined}
          data-open={filtersOpen || undefined}
          aria-expanded={filtersOpen}
          onClick={() => setFiltersOpen((o) => !o)}
        >
          <IconFilter size={14} />
          Filters
          {activeFilterGroups > 0 && (
            <Box component="span" className={styles.countChip}>
              {activeFilterGroups}
            </Box>
          )}
          <Box component="span" className={styles.chevron}>
            <IconChevronDown size={14} />
          </Box>
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <FilterDropdown view={countWindowFor(view)} />
      </Popover.Dropdown>
    </Popover>
  );

  if (filtersOnly) {
    return (
      <div className={styles.toolbar}>
        <Group justify="flex-end" wrap="nowrap">
          {filtersPill}
        </Group>
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className={styles.toolbar}>
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Stack gap={2}>
            <Group gap={6} wrap="nowrap">
              <Text fw={600} size="md" className={styles.dateLabel}>
                {getDateLabel()}
              </Text>
            </Group>
          </Stack>

          <Group gap={2} wrap="nowrap">
            <Button
              variant="default"
              size="compact-xs"
              onClick={() => onNavigate?.("PREV")}
              className={styles.navButton}
              px={6}
            >
              <IconChevronLeft size={12} />
            </Button>
            <Button
              variant="default"
              size="compact-xs"
              onClick={() => onNavigate?.("TODAY")}
              className={styles.navButton}
              px={6}
            >
              Today
            </Button>
            <Button
              variant="default"
              size="compact-xs"
              onClick={() => onNavigate?.("NEXT")}
              className={styles.navButton}
              px={6}
            >
              <IconChevronRight size={12} />
            </Button>
          </Group>
        </Group>
      </div>
    );
  }

  const tile = dateTileModel({
    view,
    date,
    weekStart: dateRange?.start,
    // The salon's calendar day, read straight from its zone.
    today: moment()
      .tz(businessInfo?.timezone || DEFAULT_TIMEZONE)
      .format(WIRE_DATE_FORMAT),
  });

  return (
    <Box className={styles.toolbar}>
      <Group justify="space-between" wrap="nowrap" gap={16}>
        <Popover
          opened={datePickerOpen}
          onChange={setDatePickerOpen}
          position="bottom-start"
          shadow="md"
          radius={10}
          withinPortal
        >
          <Popover.Target>
            <UnstyledButton
              className={styles.dateTile}
              data-open={datePickerOpen || undefined}
              aria-expanded={datePickerOpen}
              onClick={() => setDatePickerOpen((o) => !o)}
              aria-label="Change viewed date"
            >
              <Box component="span" className={styles.tileNum}>
                {tile.num}
              </Box>
              <Box component="span" className={styles.tileText}>
                <Box component="span" className={styles.tileTop}>
                  {tile.top}
                  <Box component="span" className={styles.chevron}>
                    <IconChevronDown size={15} />
                  </Box>
                </Box>
                <Box component="span" className={styles.tileMonth}>
                  {tile.month}
                  {tile.rel && (
                    <>
                      <Box component="span" className={styles.tileDot} />
                      <Box component="span" className={styles.tileRel}>
                        {tile.rel}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </UnstyledButton>
          </Popover.Target>
          <Popover.Dropdown p={6}>
            <StandardizedDatePicker
              variant="inline"
              value={moment(date).format(WIRE_DATE_FORMAT)}
              onChange={handleDatePick}
              allowPast
              maxWeeksAhead={520}
            />
          </Popover.Dropdown>
        </Popover>

        <Group gap={6} wrap="nowrap" className={styles.controls}>
          {view === "week" && <WeekDisplayMenu />}
          {filtersPill}
          {(view === "day" || view === "week") && <ZoomControl />}
          <Box className={styles.divider} />
          <UnstyledButton
            className={`${styles.control} ${styles.iconControl}`}
            onClick={() => onNavigate?.("PREV")}
            aria-label="Previous"
          >
            <IconChevronLeft size={17} />
          </UnstyledButton>
          <UnstyledButton
            className={`${styles.control} ${styles.todayControl}`}
            onClick={() => onNavigate?.("TODAY")}
          >
            Today
          </UnstyledButton>
          <UnstyledButton
            className={`${styles.control} ${styles.iconControl}`}
            onClick={() => onNavigate?.("NEXT")}
            aria-label="Next"
          >
            <IconChevronRight size={17} />
          </UnstyledButton>
        </Group>
      </Group>
    </Box>
  );
};

export default CalendarToolbar;
